import { useState, useEffect, useMemo } from 'react';
import { getConnectorConfig, saveConnectorConfigs } from '../utils/connectorConfig';
import {
  Database,
  Settings,
  Save,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  Trash2,
  X,
  Eye,
  EyeOff,
  Search,
  ChevronLeft,
  ChevronRight,
  Tag,
  PackagePlus,
  Shield,
  Loader2,
  AlertCircle,
  Edit3
} from 'lucide-react';
import axios from 'axios';
import useStore from '../store/useStore';

// Simple searchable dropdown for policies/assets
function Combobox({ options, value, onChange, placeholder, emptyText }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(query.toLowerCase()) ||
    (o.sublabel || '').toLowerCase().includes(query.toLowerCase())
  );
  const selected = options.find(o => o.value === value);
  return (
    <div className="relative">
      <div
        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 flex items-center gap-2 cursor-text"
        onClick={() => { setOpen(true); }}
      >
        <Search size={14} className="text-gray-500 flex-shrink-0" />
        <input
          className="bg-transparent flex-1 outline-none text-sm text-gray-100 placeholder-gray-500"
          placeholder={open || !selected ? placeholder : ''}
          value={open ? query : (selected ? selected.label : '')}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto combobox-scroll">
          {filtered.length === 0
            ? <p className="px-4 py-3 text-sm text-gray-500 italic">{emptyText}</p>
            : filtered.map(o => (
              <button key={o.value} onMouseDown={() => { onChange(o.value); setQuery(''); setOpen(false); }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-700 transition-colors ${value === o.value ? 'text-blue-400' : 'text-gray-200'}`}>
                {o.label}
                {o.sublabel && <span className="block text-xs text-gray-500">{o.sublabel}</span>}
              </button>
            ))
          }
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 10;

export default function Assets() {
  const { addNotification } = useStore();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerUrl, setProviderUrl] = useState(
    getConnectorConfig('assetsProviderUrl')
  );
  const [apiKey, setApiKey] = useState(
    getConnectorConfig('assetsApiKey')
  );
  const [showConfig, setShowConfig] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdAssetId, setCreatedAssetId] = useState(null);
  const [showOfferPrompt, setShowOfferPrompt] = useState(false);
  const [offerPolicies, setOfferPolicies] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerError, setOfferError] = useState('');
  const [newOffer, setNewOffer] = useState({ id: '', accessPolicyId: '', contractPolicyId: '' });
  const [apiStatus, setApiStatus] = useState('checking');
  const [showApiKey, setShowApiKey] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Shown when a delete is blocked (409) — explains why and offers next steps
  const [usageInfo, setUsageInfo] = useState(null); // { assetId, loading, definitions, agreementCount }
  // "Edit Data Source" modal — for assets that can't be deleted but whose
  // broken/expired source address needs fixing
  const [editAsset, setEditAsset] = useState(null); // { id, dataSourceType, dataAddress, minioObject, minioBucket, saving }
  const [newAsset, setNewAsset] = useState({
    id: '',
    name: '',
    description: '',
    contentType: 'application/json',
    dataSourceType: 'http',   // 'http' | 'minio'
    dataAddress: '',
    minioObject: '',
    minioBucket: 'enact-objects',
    keywords: []
  });
  const [keywordInput, setKeywordInput] = useState('');

  const MIME_TYPES = [
    { label: 'JSON',        value: 'application/json' },
    { label: 'CSV',         value: 'text/csv' },
    { label: 'Plain Text',  value: 'text/plain' },
    { label: 'XML',         value: 'application/xml' },
    { label: 'PDF',         value: 'application/pdf' },
    { label: 'MP3 Audio',   value: 'audio/mpeg' },
    { label: 'WAV Audio',   value: 'audio/wav' },
    { label: 'MP4 Video',   value: 'video/mp4' },
    { label: 'JPEG Image',  value: 'image/jpeg' },
    { label: 'PNG Image',   value: 'image/png' },
    { label: 'Binary/Other', value: 'application/octet-stream' },
  ];

  useEffect(() => {
    if (providerUrl) {
      fetchAssets();
    } else {
      setLoading(false);
    }
  }, [providerUrl]);

  // Reset to page 1 whenever search changes
  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const fetchAssets = async () => {
    if (!providerUrl) { setLoading(false); return; }

    try {
      setLoading(true);
      setApiStatus('checking');

      const isExternal = providerUrl.startsWith('http://') ||
                         !providerUrl.startsWith('https://enact-dataspace.iti.gr');
      let response;

      if (isExternal) {
        const token = localStorage.getItem('kc_token');
        response = await axios.post('/api/v1/edc-proxy', {
          targetUrl: providerUrl,
          path: '/api/management/v3/assets/request',
          method: 'POST',
          data: { '@context': {}, limit: 1000, offset: 0 },
          headers: apiKey ? { 'X-Api-Key': apiKey } : {}
        }, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      } else {
        response = await axios.post(`${providerUrl}/api/management/v3/assets/request`,
          { '@context': {}, limit: 1000, offset: 0 },
          { headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) } }
        );
      }

      const data = Array.isArray(response.data) ? response.data : [];
      if (data.length > 0) console.log('[Assets] sample asset:', JSON.stringify(data[0], null, 2));
      setAssets(data);
      setApiStatus('connected');
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      setApiStatus('error');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = () => {
    saveConnectorConfigs({ assetsProviderUrl: providerUrl, assetsApiKey: apiKey });
    setShowConfig(false);
    fetchAssets();
  };

  // ── Generic EDC management-API call (external connectors go through the
  //    /edc-proxy backend route to avoid CORS/mixed-content issues) ────────────
  const edcCall = async (method, path, data) => {
    const isExternal = providerUrl.startsWith('http://') ||
                       !providerUrl.startsWith('https://enact-dataspace.iti.gr');
    if (isExternal) {
      const token = localStorage.getItem('kc_token');
      return axios.post('/api/v1/edc-proxy', {
        targetUrl: providerUrl,
        path,
        method,
        data,
        headers: apiKey ? { 'X-Api-Key': apiKey } : {}
      }, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    }
    const url = `${providerUrl}${path}`;
    const cfg = { headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) } };
    if (method === 'GET') return axios.get(url, cfg);
    if (method === 'DELETE') return axios.delete(url, cfg);
    if (method === 'PUT') return axios.put(url, data, cfg);
    return axios.post(url, data, cfg);
  };

  // Decode the base64 assetId segment out of an EDC contract agreement @id
  // (format: "<contractDefId-b64>:<assetId-b64>:<negotiationId>").
  const decodeAgreementAssetId = (agreementId) => {
    try {
      const parts = (agreementId || '').split(':');
      if (parts.length < 2) return null;
      return atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    } catch {
      return null;
    }
  };

  // ── Keyword helpers ─────────────────────────────────────────────────────────
  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !newAsset.keywords.includes(kw)) {
      setNewAsset(prev => ({ ...prev, keywords: [...prev.keywords, kw] }));
    }
    setKeywordInput('');
  };

  const removeKeyword = (kw) => {
    setNewAsset(prev => ({ ...prev, keywords: prev.keywords.filter(k => k !== kw) }));
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    }
  };

  // ── Create asset ─────────────────────────────────────────────────────────────
  const createAsset = async () => {
    const needsUrl = newAsset.dataSourceType === 'http' && !newAsset.dataAddress;
    const needsObject = newAsset.dataSourceType === 'minio' && !newAsset.minioObject;
    if (!newAsset.id || !newAsset.name || needsUrl || needsObject) {
      addNotification({ type: 'error', message: 'Please fill in all required fields (ID, Name, and Data Address or MinIO Object)' });
      return;
    }

    try {
      const dataAddress = newAsset.dataSourceType === 'minio'
        ? { '@type': 'AmazonS3', bucketName: newAsset.minioBucket || 'enact-objects', objectName: newAsset.minioObject }
        : { '@type': 'DataAddress', type: 'HttpData', baseUrl: newAsset.dataAddress };

      const assetPayload = {
        '@context': {
          '@vocab': 'https://w3id.org/edc/v0.0.1/ns/',
          dct: 'https://purl.org/dc/terms/',
          dcat: 'https://www.w3.org/ns/dcat/',
        },
        '@id': newAsset.id,
        properties: {
          name: newAsset.name,
          'dct:title': newAsset.name,
          description: newAsset.description,
          contenttype: newAsset.contentType,
          'dct:format': newAsset.contentType,
          'http:mediaType': newAsset.contentType,
          ...(newAsset.keywords.length > 0 && { keywords: newAsset.keywords, 'dcat:keyword': newAsset.keywords })
        },
        dataAddress,
      };

      const isExternal = providerUrl.startsWith('http://') ||
                         !providerUrl.startsWith('https://enact-dataspace.iti.gr');

      if (isExternal) {
        const token = localStorage.getItem('kc_token');
        await axios.post('/api/v1/edc-proxy', {
          targetUrl: providerUrl,
          path: '/api/management/v3/assets',
          method: 'POST',
          data: assetPayload,
          headers: apiKey ? { 'X-Api-Key': apiKey } : {}
        }, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      } else {
        await axios.post(`${providerUrl}/api/management/v3/assets`, assetPayload, {
          headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) }
        });
      }

      const justCreatedId = newAsset.id;
      addNotification({ type: 'success', message: `Asset "${newAsset.name}" created successfully!` });
      setShowCreateModal(false);
      setNewAsset({ id: '', name: '', description: '', contentType: 'application/json', dataSourceType: 'http', dataAddress: '', minioObject: '', minioBucket: 'enact-objects', keywords: [] });
      setKeywordInput('');
      fetchAssets();
      setCreatedAssetId(justCreatedId);
      setNewOffer({ id: '', accessPolicyId: '', contractPolicyId: '' });
      setOfferError('');
      setShowOfferPrompt(true);
      // Fetch policies in background for the offer modal
      setOfferLoading(true);
      const isExternalPol = providerUrl.startsWith('http://') || !providerUrl.startsWith('https://enact-dataspace.iti.gr');
      const polToken = localStorage.getItem('kc_token');
      const body = { '@context': {}, limit: 1000, offset: 0 };
      try {
        let polRes;
        if (isExternalPol) {
          polRes = await axios.post('/api/v1/edc-proxy', {
            targetUrl: providerUrl, path: '/api/management/v2/policydefinitions/request',
            method: 'POST', data: body, headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          }, { headers: { 'Content-Type': 'application/json', ...(polToken ? { Authorization: `Bearer ${polToken}` } : {}) } });
        } else {
          polRes = await axios.post(`${providerUrl}/api/management/v2/policydefinitions/request`, body, {
            headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) }
          });
        }
        setOfferPolicies(Array.isArray(polRes.data) ? polRes.data : []);
      } catch { setOfferPolicies([]); } finally { setOfferLoading(false); }
    } catch (error) {
      console.error('Failed to create asset:', error);
      addNotification({ type: 'error', message: `Failed to create asset: ${error.response?.data?.message || error.message}` });
    }
  };

  const deleteAsset = async (assetId, opts = {}) => {
    if (!opts.skipConfirm && !confirm(`Are you sure you want to delete asset "${assetId}"?`)) return;

    try {
      await edcCall('DELETE', `/api/management/v3/assets/${encodeURIComponent(assetId)}`);
      addNotification({ type: 'success', message: `Asset "${assetId}" deleted.` });
      setUsageInfo(null);
      fetchAssets();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      if (error.response?.status === 409) {
        // Don't just show a generic message — find out *why* it's blocked
        // (an active contract definition vs. real transaction history) so
        // we can offer the right next step instead of a dead end.
        investigateAssetUsage(assetId);
        return;
      }
      const msg = error.response?.data?.message
        ? `Failed to delete asset: ${error.response.data.message}`
        : `Failed to delete asset: ${error.message}`;
      addNotification({ type: 'error', message: msg });
    }
  };

  // When a delete is blocked, figure out whether it's an active contract
  // definition (removable) or real transaction history (not removable —
  // EDC keeps agreements as an immutable audit trail).
  const investigateAssetUsage = async (assetId) => {
    setUsageInfo({ assetId, loading: true, definitions: [], agreementCount: 0 });
    try {
      const [defsRes, agreementsRes] = await Promise.all([
        edcCall('POST', '/api/management/v2/contractdefinitions/request', { '@context': {}, limit: 1000, offset: 0 }),
        edcCall('POST', '/api/management/v2/contractagreements/request', { '@context': {}, limit: 1000, offset: 0 }),
      ]);

      const defs = Array.isArray(defsRes.data) ? defsRes.data : [];
      const matchingDefs = defs.filter(d => {
        const sel = d['edc:assetsSelector'] ?? d.assetsSelector;
        const criteria = Array.isArray(sel) ? sel : [sel].filter(Boolean);
        return criteria.some(c => (c?.['edc:operandRight'] ?? c?.operandRight) === assetId);
      });

      const agreements = Array.isArray(agreementsRes.data) ? agreementsRes.data : [];
      const agreementCount = agreements.filter(a => decodeAgreementAssetId(a['@id']) === assetId).length;

      setUsageInfo({ assetId, loading: false, definitions: matchingDefs, agreementCount });
    } catch (error) {
      console.error('Failed to look up why the asset is in use:', error);
      setUsageInfo({ assetId, loading: false, definitions: [], agreementCount: 0, error: true });
    }
  };

  const deleteDefinitionAndRetry = async (defId) => {
    try {
      await edcCall('DELETE', `/api/management/v2/contractdefinitions/${encodeURIComponent(defId)}`);
      addNotification({ type: 'success', message: `Contract definition "${defId}" deleted.` });
      if (usageInfo) await investigateAssetUsage(usageInfo.assetId);
    } catch (error) {
      console.error('Failed to delete contract definition:', error);
      addNotification({ type: 'error', message: `Failed to delete contract definition: ${error.response?.data?.message || error.message}` });
    }
  };

  // ── Edit Data Source ──────────────────────────────────────────────────────────
  const openEditAsset = (assetId) => {
    setEditAsset({ id: assetId, dataSourceType: 'http', dataAddress: '', minioObject: '', minioBucket: 'enact-objects', saving: false, loading: true });
    edcCall('GET', `/api/management/v3/assets/${encodeURIComponent(assetId)}`)
      .then(res => {
        const addr = res.data?.['edc:dataAddress'] ?? res.data?.dataAddress ?? {};
        const type = addr['edc:type'] ?? addr.type;
        const isMinio = type === 'AmazonS3';
        setEditAsset({
          id: assetId,
          dataSourceType: isMinio ? 'minio' : 'http',
          dataAddress: addr['edc:baseUrl'] ?? addr.baseUrl ?? '',
          minioObject: addr['edc:objectName'] ?? addr.objectName ?? '',
          minioBucket: addr['edc:bucketName'] ?? addr.bucketName ?? 'enact-objects',
          saving: false,
          loading: false,
        });
      })
      .catch(error => {
        console.error('Failed to load asset for editing:', error);
        addNotification({ type: 'error', message: `Failed to load asset "${assetId}" for editing: ${error.message}` });
        setEditAsset(null);
      });
  };

  const saveEditAsset = async () => {
    if (!editAsset) return;
    const needsUrl = editAsset.dataSourceType === 'http' && !editAsset.dataAddress;
    const needsObject = editAsset.dataSourceType === 'minio' && !editAsset.minioObject;
    if (needsUrl || needsObject) {
      addNotification({ type: 'error', message: 'Please provide a data address or MinIO object.' });
      return;
    }

    setEditAsset(prev => ({ ...prev, saving: true }));
    try {
      const getRes = await edcCall('GET', `/api/management/v3/assets/${encodeURIComponent(editAsset.id)}`);
      const existing = getRes.data;
      // Match whichever key convention the connector returned (compact
      // `dataAddress`/`type` vs. expanded `edc:dataAddress`/`edc:type`)
      // instead of assuming one, so we don't silently drop the update.
      const addrKey = existing['edc:dataAddress'] ? 'edc:dataAddress' : 'dataAddress';
      const usesEdcPrefix = Object.keys(existing[addrKey] || {}).some(k => k.startsWith('edc:'));
      const typeKey = usesEdcPrefix ? 'edc:type' : 'type';
      const baseUrlKey = usesEdcPrefix ? 'edc:baseUrl' : 'baseUrl';
      const bucketKey = usesEdcPrefix ? 'edc:bucketName' : 'bucketName';
      const objectKey = usesEdcPrefix ? 'edc:objectName' : 'objectName';

      const newDataAddress = editAsset.dataSourceType === 'minio'
        ? { '@type': usesEdcPrefix ? 'edc:DataAddress' : 'DataAddress', [typeKey]: 'AmazonS3', [bucketKey]: editAsset.minioBucket || 'enact-objects', [objectKey]: editAsset.minioObject }
        : { '@type': usesEdcPrefix ? 'edc:DataAddress' : 'DataAddress', [typeKey]: 'HttpData', [baseUrlKey]: editAsset.dataAddress };

      const updated = { ...existing, [addrKey]: newDataAddress };
      await edcCall('PUT', `/api/management/v3/assets/${encodeURIComponent(editAsset.id)}`, updated);

      addNotification({ type: 'success', message: `Data source updated for "${editAsset.id}".` });
      setEditAsset(null);
      setUsageInfo(null);
      fetchAssets();
    } catch (error) {
      console.error('Failed to update asset data source:', error);
      addNotification({ type: 'error', message: `Failed to update data source: ${error.response?.data?.message || error.message}` });
      setEditAsset(prev => prev ? { ...prev, saving: false } : prev);
    }
  };

  // ── Property bag helper (EDC returns edc:properties, not properties) ─────────
  const getProps = (asset) => asset['edc:properties'] || asset.properties || {};

  const getProp = (asset, ...keys) => {
    const props = getProps(asset);
    for (const k of keys) {
      if (props[k] !== undefined && props[k] !== null) return props[k];
    }
    return undefined;
  };

  const getKeywords = (asset) => {
    const props = getProps(asset);
    const kwKey = Object.keys(props).find(k =>
      k === 'keywords' || k === 'edc:keywords' || k.toLowerCase().includes('keyword')
    );
    if (!kwKey) return [];
    const val = props[kwKey];
    return Array.isArray(val) ? val : typeof val === 'string' ? [val] : [];
  };

  // ── Search + pagination ───────────────────────────────────────────────────────
  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return assets;
    const q = searchQuery.toLowerCase();
    return assets.filter(asset => {
      const id = (asset['@id'] || '').toLowerCase();
      const name = (
        getProp(asset, 'http://purl.org/dc/terms/title', 'edc:name', 'name', 'asset:prop:name') || ''
      ).toLowerCase();
      const desc = (
        getProp(asset, 'http://purl.org/dc/terms/description', 'edc:description', 'description', 'asset:prop:description') || ''
      ).toLowerCase();
      const kwMatch = getKeywords(asset).some(k => k.toLowerCase().includes(q));
      // Scan all string/array property values as fallback
      const propMatch = Object.values(getProps(asset)).some(v => {
        if (typeof v === 'string') return v.toLowerCase().includes(q);
        if (Array.isArray(v)) return v.some(i => typeof i === 'string' && i.toLowerCase().includes(q));
        return false;
      });
      return id.includes(q) || name.includes(q) || desc.includes(q) || kwMatch || propMatch;
    });
  }, [assets, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredAssets.length / PAGE_SIZE));
  const pagedAssets = filteredAssets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckCircle className="text-green-400" size={20} />;
      case 'error':     return <AlertTriangle className="text-red-400" size={20} />;
      default:          return <Clock className="text-yellow-400 animate-pulse" size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected': return 'Connected';
      case 'error':     return 'Connection Error';
      default:          return 'Checking...';
    }
  };

  const createOffer = async () => {
    if (!newOffer.id || !newOffer.accessPolicyId || !newOffer.contractPolicyId) {
      setOfferError('Please fill in all required fields.');
      return;
    }
    setOfferLoading(true);
    setOfferError('');
    try {
      const token = localStorage.getItem('kc_token');
      const isExternal = providerUrl.startsWith('http://') || !providerUrl.startsWith('https://enact-dataspace.iti.gr');
      const payload = {
        '@context': { '@vocab': 'https://w3id.org/edc/v0.0.1/ns/' },
        '@id': newOffer.id,
        accessPolicyId: newOffer.accessPolicyId,
        contractPolicyId: newOffer.contractPolicyId,
        assetsSelector: [{ operandLeft: 'https://w3id.org/edc/v0.0.1/ns/id', operator: '=', operandRight: createdAssetId }]
      };
      if (isExternal) {
        await axios.post('/api/v1/edc-proxy', {
          targetUrl: providerUrl, path: '/api/management/v2/contractdefinitions',
          method: 'POST', data: payload, headers: apiKey ? { 'X-Api-Key': apiKey } : {}
        }, { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      } else {
        await axios.post(`${providerUrl}/api/management/v2/contractdefinitions`, payload, {
          headers: { 'Content-Type': 'application/json', ...(apiKey ? { 'X-Api-Key': apiKey } : {}) }
        });
      }
      addNotification({ type: 'success', message: `Data offer created for asset "${createdAssetId}".` });
      setShowOfferPrompt(false);
    } catch (err) {
      setOfferError(err.response?.data?.message || err.message || 'Failed to create data offer.');
    } finally {
      setOfferLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-white mb-2">EDC Assets</h1>
          <p className="text-gray-400">View assets from Provider EDC connector</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowConfig(!showConfig)} className="btn btn-secondary">
            <Settings size={20} />Configure Connector
          </button>
          {providerUrl && (
            <>
              <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
                <Plus size={20} />Create Asset
              </button>
              <button onClick={fetchAssets} className="btn btn-secondary" disabled={loading}>
                <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <div className="card border-blue-500/30">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings size={20} className="text-blue-400" />Provider EDC Configuration
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Management API URL *</label>
              <input type="text" value={providerUrl} onChange={e => setProviderUrl(e.target.value)}
                
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">API Key (optional)</label>
              <div className="relative">
                <input type={showApiKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)}
                  placeholder=""
                  className="w-full px-4 py-2 pr-12 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                <button type="button" onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors">
                  {showApiKey ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={saveConfiguration} className="btn btn-primary"><Save size={20} />Save & Connect</button>
              <button onClick={() => setShowConfig(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* No connector warning */}
      {!providerUrl && !showConfig && (
        <div className="card border-yellow-500/30 bg-yellow-900/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">No Provider Connector Configured</h3>
              <p className="text-gray-300 mb-3">Please configure your Provider EDC connector endpoint to view assets.</p>
              <button onClick={() => setShowConfig(true)} className="btn btn-primary"><Settings size={20} />Configure Now</button>
            </div>
          </div>
        </div>
      )}

      {/* API Status */}
      {providerUrl && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Provider EDC Connector</p>
              <p className="text-lg font-semibold mt-1">{getStatusText(apiStatus)}</p>
            </div>
            {getStatusIcon(apiStatus)}
          </div>
        </div>
      )}

      {/* Post-creation: Publish Data Offer modal */}
      {showOfferPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-lg w-full mx-4 border-blue-500/30 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <PackagePlus size={24} className="text-blue-400" />Publish New Data Offer
              </h3>
              <button onClick={() => setShowOfferPrompt(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Asset created banner */}
            <div className="flex items-center gap-2 bg-green-900/20 border border-green-800 rounded-lg px-3 py-2 mb-4">
              <CheckCircle size={15} className="text-green-400 flex-shrink-0" />
              <p className="text-sm text-green-300">
                Asset <span className="font-mono font-semibold">{createdAssetId}</span> created — now publish it as a data offer.
              </p>
            </div>

            {offerLoading && offerPolicies.length === 0 ? (
              <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
                <Loader2 className="animate-spin" size={24} />Loading policies…
              </div>
            ) : (
              <div className="space-y-4">
                {/* ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">ID *</label>
                  <input type="text" value={newOffer.id}
                    onChange={e => setNewOffer(p => ({ ...p, id: e.target.value }))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>

                {/* Access Policy */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    <Shield size={14} className="text-blue-400" />Access Policy *
                  </label>
                  {offerPolicies.length === 0 ? (
                    <p className="text-sm text-yellow-400 italic">No policies found. Create a policy first.</p>
                  ) : (
                    <Combobox
                      options={offerPolicies.map(p => ({ value: p['@id'] || p.id, label: p['@id'] || p.id }))}
                      value={newOffer.accessPolicyId}
                      onChange={v => setNewOffer(p => ({ ...p, accessPolicyId: v }))}
                      placeholder="Search access policy…"
                      emptyText="No matching policies"
                    />
                  )}
                </div>

                {/* Contract Policy */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    <Shield size={14} className="text-purple-400" />Contract Policy *
                  </label>
                  {offerPolicies.length === 0 ? (
                    <p className="text-sm text-yellow-400 italic">No policies found. Create a policy first.</p>
                  ) : (
                    <Combobox
                      options={offerPolicies.map(p => ({ value: p['@id'] || p.id, label: p['@id'] || p.id }))}
                      value={newOffer.contractPolicyId}
                      onChange={v => setNewOffer(p => ({ ...p, contractPolicyId: v }))}
                      placeholder="Search contract policy…"
                      emptyText="No matching policies"
                    />
                  )}
                </div>

                {/* Asset — pre-filled, read-only */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                    <Database size={14} className="text-green-400" />Asset *
                  </label>
                  <div className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-green-400 font-mono">
                    {createdAssetId}
                  </div>
                </div>

                {offerError && (
                  <div className="bg-red-900/20 border border-red-800 rounded-lg p-3 flex gap-2">
                    <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                    <p className="text-red-400 text-sm">{offerError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button onClick={createOffer} disabled={offerLoading} className="btn btn-primary flex-1">
                    {offerLoading ? <Loader2 className="animate-spin" size={18} /> : <PackagePlus size={18} />}
                    Create
                  </button>
                  <button onClick={() => setShowOfferPrompt(false)} className="btn btn-secondary flex-1">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Asset-in-use explainer — shown when a delete comes back 409 */}
      {usageInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-lg w-full mx-4 border-yellow-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2 text-yellow-400">
                <AlertTriangle size={22} />Can't delete this asset
              </h3>
              <button onClick={() => setUsageInfo(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>

            {usageInfo.loading ? (
              <div className="flex items-center gap-2 text-gray-400 py-6 justify-center">
                <Loader2 className="animate-spin" size={20} />Checking why…
              </div>
            ) : usageInfo.error ? (
              <p className="text-sm text-gray-400">
                Couldn't determine why "{usageInfo.assetId}" is in use — check its contract definitions and agreements manually.
              </p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-300">
                  "<span className="font-mono text-gray-100">{usageInfo.assetId}</span>" can't be deleted yet.
                </p>

                {usageInfo.definitions.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-300 mb-2">
                      Blocking contract definition{usageInfo.definitions.length > 1 ? 's' : ''}:
                    </p>
                    <div className="space-y-2">
                      {usageInfo.definitions.map(d => (
                        <div key={d['@id']} className="flex items-center justify-between px-3 py-2 bg-gray-800/50 rounded-lg border border-gray-700">
                          <span className="text-sm font-mono text-gray-200 truncate">{d['@id']}</span>
                          <button onClick={() => deleteDefinitionAndRetry(d['@id'])}
                            className="text-xs px-2 py-1 bg-red-900/30 hover:bg-red-900/50 text-red-300 rounded flex items-center gap-1 flex-shrink-0 ml-2">
                            <Trash2 size={12} />Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {usageInfo.agreementCount > 0 && (
                  <div className="flex items-start gap-2 px-3 py-2 bg-blue-900/20 border border-blue-800 rounded-lg">
                    <Shield size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-300">
                      This asset has already been used in <strong>{usageInfo.agreementCount}</strong> contract agreement{usageInfo.agreementCount > 1 ? 's' : ''}.
                      EDC keeps those as an audit trail, so it can't be deleted — edit its data source instead if it's broken.
                    </p>
                  </div>
                )}

                {usageInfo.definitions.length === 0 && usageInfo.agreementCount === 0 && (
                  <p className="text-sm text-gray-400">
                    EDC reported this asset as in use, but no contract definition or agreement currently references it — that may have just changed. Try deleting again.
                  </p>
                )}

                <div className="flex gap-3 pt-2">
                  {usageInfo.definitions.length === 0 && usageInfo.agreementCount === 0 && (
                    <button onClick={() => deleteAsset(usageInfo.assetId, { skipConfirm: true })} className="btn btn-primary flex-1">
                      <RefreshCw size={16} />Retry Delete
                    </button>
                  )}
                  <button onClick={() => { const id = usageInfo.assetId; setUsageInfo(null); openEditAsset(id); }} className="btn btn-secondary flex-1">
                    <Edit3 size={16} />Edit Data Source
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Data Source Modal */}
      {editAsset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-lg w-full mx-4 border-blue-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Edit3 size={22} className="text-blue-400" />Edit Data Source
              </h3>
              <button onClick={() => setEditAsset(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>

            {editAsset.loading ? (
              <div className="flex items-center gap-2 text-gray-400 py-6 justify-center">
                <Loader2 className="animate-spin" size={20} />Loading…
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-gray-500 font-mono truncate">{editAsset.id}</p>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data Source</label>
                  <div className="flex gap-2 mb-2">
                    <button type="button"
                      onClick={() => setEditAsset(prev => ({ ...prev, dataSourceType: 'http' }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${editAsset.dataSourceType === 'http' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                      HTTP URL
                    </button>
                    <button type="button"
                      onClick={() => setEditAsset(prev => ({ ...prev, dataSourceType: 'minio' }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${editAsset.dataSourceType === 'minio' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                      MinIO Object
                    </button>
                  </div>
                  {editAsset.dataSourceType === 'http' ? (
                    <input type="text" value={editAsset.dataAddress} onChange={e => setEditAsset(prev => ({ ...prev, dataAddress: e.target.value }))}
                      placeholder="https://…"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  ) : (
                    <div className="space-y-2">
                      <input type="text" value={editAsset.minioObject} onChange={e => setEditAsset(prev => ({ ...prev, minioObject: e.target.value }))}
                        placeholder="Object key (path/in/bucket.json)"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      <input type="text" value={editAsset.minioBucket} onChange={e => setEditAsset(prev => ({ ...prev, minioBucket: e.target.value }))}
                        placeholder="Bucket name"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    If this used a presigned URL that expired, generate a fresh one and paste it here.
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={saveEditAsset} disabled={editAsset.saving} className="btn btn-primary flex-1 disabled:opacity-50">
                    {editAsset.saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                    Save
                  </button>
                  <button onClick={() => setEditAsset(null)} className="btn btn-secondary flex-1">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Asset Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-2xl w-full mx-4 border-blue-500/30 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                <Plus size={24} className="text-blue-400" />Create New Asset
              </h3>
              <button onClick={() => { setShowCreateModal(false); setKeywordInput(''); setNewAsset({ id: '', name: '', description: '', contentType: 'application/json', dataSourceType: 'http', dataAddress: '', minioObject: '', minioBucket: 'enact-objects', keywords: [] }); }}
                className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Asset ID *</label>
                <input type="text" value={newAsset.id} onChange={e => setNewAsset(prev => ({ ...prev, id: e.target.value }))}
                  
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                <input type="text" value={newAsset.name} onChange={e => setNewAsset(prev => ({ ...prev, name: e.target.value }))}
                  
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea value={newAsset.description} onChange={e => setNewAsset(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data Format / MIME Type</label>
                <select value={newAsset.contentType} onChange={e => setNewAsset(prev => ({ ...prev, contentType: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  {MIME_TYPES.map(m => (
                    <option key={m.value} value={m.value}>{m.label} ({m.value})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Data Source *</label>
                <div className="flex gap-2 mb-2">
                  <button type="button"
                    onClick={() => setNewAsset(prev => ({ ...prev, dataSourceType: 'http' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${newAsset.dataSourceType === 'http' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    HTTP URL
                  </button>
                  <button type="button"
                    onClick={() => setNewAsset(prev => ({ ...prev, dataSourceType: 'minio' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${newAsset.dataSourceType === 'minio' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    MinIO Object
                  </button>
                </div>
                {newAsset.dataSourceType === 'http' ? (
                  <input type="text" value={newAsset.dataAddress} onChange={e => setNewAsset(prev => ({ ...prev, dataAddress: e.target.value }))}
                    
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                ) : (
                  <div className="space-y-2">
                    <input type="text" value={newAsset.minioObject} onChange={e => setNewAsset(prev => ({ ...prev, minioObject: e.target.value }))}
                      
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    <input type="text" value={newAsset.minioBucket} onChange={e => setNewAsset(prev => ({ ...prev, minioBucket: e.target.value }))}
                      
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                )}
              </div>
              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-1">
                  <Tag size={14} className="text-blue-400" />Keywords
                </label>
                <div className="flex gap-2">
                  <input type="text" value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    placeholder="Type a keyword and press Enter or comma"
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  <button type="button" onClick={addKeyword}
                    className="px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg transition-colors text-sm">
                    Add
                  </button>
                </div>
                {newAsset.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newAsset.keywords.map(kw => (
                      <span key={kw} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-900/40 text-blue-300 rounded-full text-xs">
                        {kw}
                        <button type="button" onClick={() => removeKeyword(kw)} className="hover:text-white ml-0.5">
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Press Enter or comma to add each keyword.</p>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={createAsset} className="btn btn-primary flex-1"><Save size={20} />Create Asset</button>
                <button onClick={() => { setShowCreateModal(false); setKeywordInput(''); setNewAsset({ id: '', name: '', description: '', contentType: 'application/json', dataSourceType: 'http', dataAddress: '', minioObject: '', minioBucket: 'enact-objects', keywords: [] }); }}
                  className="btn btn-secondary flex-1">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assets List */}
      {providerUrl && assets.length > 0 && (
        <div className="card">
          {/* List header + search */}
          <div className="flex items-center justify-between mb-4 gap-4">
            <h2 className="text-xl font-semibold flex items-center gap-2 shrink-0">
              <Database className="text-blue-400" size={24} />
              Assets ({filteredAssets.length}{filteredAssets.length !== assets.length ? ` / ${assets.length}` : ''})
            </h2>
            <div className="relative max-w-sm w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by ID, name, description or keyword…"
                className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {filteredAssets.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">No assets match your search.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pagedAssets.map((asset) => {
                  const kwList = getKeywords(asset);
                  const title = getProp(asset,
                    'http://purl.org/dc/terms/title', 'edc:name', 'name', 'asset:prop:name'
                  ) || asset['@id'];
                  const description = getProp(asset,
                    'http://purl.org/dc/terms/description', 'edc:description', 'description', 'asset:prop:description'
                  ) || 'No description';
                  const contentType = getProp(asset,
                    'dct:format', 'http:mediaType', 'edc:contenttype', 'contenttype', 'asset:prop:contenttype'
                  ) || 'application/json';
                  const formatColor = contentType.startsWith('video/') ? 'bg-red-900/30 text-red-300'
                    : contentType.startsWith('audio/') ? 'bg-yellow-900/30 text-yellow-300'
                    : contentType.startsWith('image/') ? 'bg-green-900/30 text-green-300'
                    : contentType === 'text/csv' ? 'bg-teal-900/30 text-teal-300'
                    : 'bg-blue-900/30 text-gray-400';
                  return (
                    <div key={asset['@id']} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-blue-500/50 transition-colors group">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-white truncate flex-1">{title}</h3>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0">
                          <button onClick={() => openEditAsset(asset['@id'])}
                            className="text-gray-400 hover:text-blue-400" title="Edit data source">
                            <Edit3 size={16} />
                          </button>
                          <button onClick={() => deleteAsset(asset['@id'])}
                            className="text-red-400 hover:text-red-300" title="Delete asset">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-400 mb-2 line-clamp-2">{description}</p>
                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${formatColor}`}>
                          {contentType}
                        </span>
                        {kwList.map(kw => (
                          <span key={kw} className="keyword-tag px-2 py-0.5 bg-purple-900/30 text-purple-300 rounded-full">{kw}</span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-600 mt-2 truncate">ID: {asset['@id']}</p>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
                  <p className="text-sm text-gray-400">
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredAssets.length)} of {filteredAssets.length}
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="p-2 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((item, idx) =>
                        item === '…' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">…</span>
                        ) : (
                          <button key={item} onClick={() => setCurrentPage(item)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${item === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-700 text-gray-300'}`}>
                            {item}
                          </button>
                        )
                      )}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="p-2 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {providerUrl && !loading && assets.length === 0 && apiStatus === 'connected' && (
        <div className="card text-center py-12">
          <Database className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No assets found in the configured Provider EDC connector.</p>
        </div>
      )}
    </div>
  );
}
